import { useState, useRef, useEffect, useCallback } from 'react';
import Breadcrumb from '../../components/feature/Breadcrumb';
import ToolsDropdown from '../../components/feature/ToolsDropdown';
import LoginModal from '../home/components/LoginModal';
import CheckoutModal from '../home/components/CheckoutModal';
import EmailCaptureModal from '../home/components/EmailCaptureModal';
import SavedLibraryDrawer, { SavedResult } from './components/SavedLibraryDrawer';
import SearchHistoryPanel, { HistoryEntry } from './components/SearchHistoryPanel';
import RelatedSearchSuggestions from './components/RelatedSearchSuggestions';
import TrendingSearches from './components/TrendingSearches';
import AdvancedSearchPanel, { AdvancedFilters } from './components/AdvancedSearchPanel';
import KeyboardShortcutsLegend from './components/KeyboardShortcutsLegend';

/* ─── shared constants (mirrors home/page.tsx) ─── */
const WORDS_USED_KEY = 'openclaw_words_used';
const FREE_WORD_LIMIT = 1500;
const FIRST_USAGE_KEY = 'openclaw_first_usage';
const EMAIL_CAPTURED_KEY = 'openclaw_email_captured';
const SAVED_RESULTS_KEY = 'openclaw_saved_results';
const SEARCH_HISTORY_KEY = 'openclaw_search_history';
const MAX_HISTORY = 10;

/* ─── helpers ─── */
const countWords = (text: string) => text.trim().split(/\s+/).filter(Boolean).length;

interface ResultItem {
  id: number;
  title: string;
  source: string;
  date: string;
  type: string;
  snippet: string;
  url: string;
  saved: boolean;
  citations: number;
}

const MOCK_RESULTS: Omit<ResultItem, 'saved'>[] = [
  {
    id: 1,
    title: 'Quantum Computing: Recent Breakthroughs and Future Prospects',
    source: 'Nature Physics',
    date: '2024',
    type: 'Academic Paper',
    snippet: 'This comprehensive review examines the latest developments in quantum computing, including advances in qubit stability, error correction techniques, and practical applications in cryptography.',
    url: 'https://nature.com/articles/quantum-computing',
    citations: 312,
  },
  {
    id: 2,
    title: 'Google Achieves Quantum Supremacy with 53-Qubit Processor',
    source: 'Science Magazine',
    date: '2023',
    type: 'Research Study',
    snippet: 'Google researchers demonstrate quantum supremacy by performing a calculation in 200 seconds that would take classical supercomputers thousands of years to complete.',
    url: 'https://sciencemag.org/google-quantum',
    citations: 87,
  },
  {
    id: 3,
    title: 'IBM Unveils 433-Qubit Quantum Processor "Osprey"',
    source: 'MIT Technology Review',
    date: '2024',
    type: 'News Article',
    snippet: 'IBM has announced its most powerful quantum processor to date, marking a significant milestone in the race toward practical quantum computing applications in enterprise environments.',
    url: 'https://technologyreview.com/ibm-osprey',
    citations: 14,
  },
];

const CITATION_STYLES: Record<string, (r: ResultItem) => string> = {
  APA: (r) => `${r.source}. (${r.date}). ${r.title}. Retrieved from ${r.url}`,
  MLA: (r) => `"${r.title}." ${r.source}, ${r.date}, ${r.url}.`,
  Chicago: (r) => `${r.source}. "${r.title}." ${r.date}. ${r.url}.`,
};

export default function DigitalAISearch() {
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<Array<{ file: File; preview?: string; id: string }>>([]);
  const [showAttachments, setShowAttachments] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* active quick filters */
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());

  /* results + per-result saved/cite state */
  const [results, setResults] = useState<ResultItem[]>([]);
  const [citeTarget, setCiteTarget] = useState<ResultItem | null>(null);
  const [citeStyle, setCiteStyle] = useState<'APA' | 'MLA' | 'Chicago'>('APA');
  const [citeCopied, setCiteCopied] = useState(false);
  const [openTarget, setOpenTarget] = useState<ResultItem | null>(null);

  /* word tracking */
  const [wordsUsed, setWordsUsed] = useState(0);
  const [emailCaptured, setEmailCaptured] = useState(false);

  /* modals */
  const [showLogin, setShowLogin] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showEmailCapture, setShowEmailCapture] = useState(false);

  /* ── NEW: saved library, search history, suggestions ── */
  const [savedItems, setSavedItems] = useState<SavedResult[]>([]);
  const [searchHistory, setSearchHistory] = useState<HistoryEntry[]>([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultFilterRef = useRef<HTMLInputElement>(null);

  /* ── Keyboard Shortcuts Legend ── */
  const [showShortcutsLegend, setShowShortcutsLegend] = useState(false);

  /* ── Advanced Search ── */
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advFilters, setAdvFilters] = useState<AdvancedFilters>({
    dateFrom: null,
    dateTo: null,
    excludedTypes: new Set<string>(),
    minCitations: 0,
  });

  const advActiveCount =
    (advFilters.dateFrom !== null ? 1 : 0) +
    (advFilters.dateTo !== null ? 1 : 0) +
    advFilters.excludedTypes.size +
    (advFilters.minCitations > 0 ? 1 : 0);

  const resetAdvFilters = () =>
    setAdvFilters({ dateFrom: null, dateTo: null, excludedTypes: new Set(), minCitations: 0 });

  /* ─── on mount: read usage from localStorage ─── */
  useEffect(() => {
    const stored = localStorage.getItem(WORDS_USED_KEY);
    if (stored) setWordsUsed(parseInt(stored, 10) || 0);
    if (localStorage.getItem(EMAIL_CAPTURED_KEY)) setEmailCaptured(true);

    /* load saved library */
    try {
      const savedRaw = localStorage.getItem(SAVED_RESULTS_KEY);
      if (savedRaw) setSavedItems(JSON.parse(savedRaw));
    } catch { /* ignore */ }

    /* load search history */
    try {
      const histRaw = localStorage.getItem(SEARCH_HISTORY_KEY);
      if (histRaw) setSearchHistory(JSON.parse(histRaw));
    } catch { /* ignore */ }
  }, []);

  /* ─── click-outside: close suggestion & history panels ─── */
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
        setShowHistoryPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  /* ─── global keyboard shortcuts ─── */
  useEffect(() => {
    const isTyping = () => {
      const el = document.activeElement;
      return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement;
    };

    const handleKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;

      /* Ctrl/Cmd + K → focus search */
      if (mod && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
        return;
      }

      /* Ctrl/Cmd + F → focus results filter (only when results are showing) */
      if (mod && e.key === 'f') {
        if (resultFilterRef.current) {
          e.preventDefault();
          resultFilterRef.current.focus();
          resultFilterRef.current.select();
        }
        return;
      }

      /* Ctrl/Cmd + Enter → trigger search */
      if (mod && e.key === 'Enter') {
        e.preventDefault();
        handleSearch();
        return;
      }

      /* Ctrl/Cmd + Shift + A → toggle advanced search */
      if (mod && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setShowAdvanced(v => !v);
        return;
      }

      /* Ctrl/Cmd + Shift + L → open library */
      if (mod && e.shiftKey && e.key === 'L') {
        e.preventDefault();
        setShowLibrary(true);
        return;
      }

      /* / → focus search (when not already typing) */
      if (e.key === '/' && !isTyping()) {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }

      /* ? → toggle shortcuts legend (when not typing) */
      if (e.key === '?' && !isTyping()) {
        e.preventDefault();
        setShowShortcutsLegend(v => !v);
        return;
      }

      /* Escape → dismiss in order */
      if (e.key === 'Escape') {
        if (showShortcutsLegend) { setShowShortcutsLegend(false); return; }
        if (showSuggestions || showHistoryPanel) {
          setShowSuggestions(false);
          setShowHistoryPanel(false);
          return;
        }
        if (resultFilterRef.current === document.activeElement) {
          setResultFilter('');
          resultFilterRef.current.blur();
          return;
        }
        if (showAdvanced) { setShowAdvanced(false); return; }
        if (showLibrary) { setShowLibrary(false); return; }
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showShortcutsLegend, showSuggestions, showHistoryPanel, showAdvanced, showLibrary]);

  /* ─── derived values ─── */
  const remaining = Math.max(0, FREE_WORD_LIMIT - wordsUsed);
  const usedPct = Math.min((wordsUsed / FREE_WORD_LIMIT) * 100, 100);
  const isOver = wordsUsed >= FREE_WORD_LIMIT;
  const isWarning = usedPct >= 70 && !isOver;

  /* ─── search-within-results ─── */
  const [resultFilter, setResultFilter] = useState('');

  const filteredResults = results.filter(r => {
    /* keyword filter */
    if (resultFilter.trim()) {
      const q = resultFilter.toLowerCase();
      const matchesKeyword =
        r.title.toLowerCase().includes(q) ||
        r.snippet.toLowerCase().includes(q) ||
        r.source.toLowerCase().includes(q) ||
        r.type.toLowerCase().includes(q);
      if (!matchesKeyword) return false;
    }
    /* advanced: date range */
    const year = parseInt(r.date);
    if (advFilters.dateFrom !== null && !isNaN(year) && year < advFilters.dateFrom) return false;
    if (advFilters.dateTo !== null && !isNaN(year) && year > advFilters.dateTo) return false;
    /* advanced: excluded types */
    if (advFilters.excludedTypes.has(r.type)) return false;
    /* advanced: min citations */
    if (advFilters.minCitations > 0 && r.citations < advFilters.minCitations) return false;
    return true;
  });

  /* ─── usage stats for trending section ─── */
  const STOP_WORDS = new Set(['the','a','an','and','or','for','of','in','on','at','to','with','from','is','are','was','were','be','been','by','that','this','what','how','latest','recent','results','research','study','into','its','about','more','their']);

  const todaySearchCount = searchHistory.filter(e => {
    const d = new Date(e.timestamp);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  }).length;

  const topTopics: string[] = (() => {
    const freq: Record<string, number> = {};
    searchHistory.forEach(e => {
      e.query.toLowerCase().split(/\s+/).forEach(word => {
        const clean = word.replace(/[^a-z]/g, '');
        if (clean.length > 3 && !STOP_WORDS.has(clean)) {
          freq[clean] = (freq[clean] || 0) + 1;
        }
      });
    });
    return Object.entries(freq)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([w]) => w.charAt(0).toUpperCase() + w.slice(1));
  })();

  /* ─── file helpers ─── */
  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;
    const newFiles = Array.from(files).filter(f => {
      if (f.size > 25 * 1024 * 1024) return false;
      const valid = ['image/jpeg','image/png','image/gif','image/webp','video/mp4','video/quicktime','video/x-msvideo','video/webm','application/pdf','application/vnd.openxmlformats-officedocument.wordprocessingml.document','text/plain','text/csv'];
      return valid.includes(f.type);
    });
    if (attachedFiles.length + newFiles.length > 10) return;
    const withPreviews = newFiles.map(f => {
      const id = Math.random().toString(36).substr(2, 9);
      return f.type.startsWith('image/') ? { file: f, preview: URL.createObjectURL(f), id } : { file: f, id };
    });
    setAttachedFiles(prev => [...prev, ...withPreviews]);
  };

  const removeFile = (id: string) => {
    setAttachedFiles(prev => {
      const f = prev.find(x => x.id === id);
      if (f?.preview) URL.revokeObjectURL(f.preview);
      return prev.filter(x => x.id !== id);
    });
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('video/')) return 'ri-video-line';
    if (type === 'application/pdf') return 'ri-file-pdf-line';
    if (type.includes('word')) return 'ri-file-word-line';
    if (type === 'text/csv') return 'ri-file-excel-line';
    return 'ri-file-text-line';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  /* ─── filter toggle ─── */
  const toggleFilter = (filter: string) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      next.has(filter) ? next.delete(filter) : next.add(filter);
      return next;
    });
  };

  /* ─── saved library helpers ─── */
  const persistSaved = useCallback((items: SavedResult[]) => {
    try { localStorage.setItem(SAVED_RESULTS_KEY, JSON.stringify(items)); } catch { /* ignore */ }
  }, []);

  const addToLibrary = useCallback((result: ResultItem) => {
    setSavedItems(prev => {
      if (prev.some(s => s.id === result.id)) return prev;
      const next: SavedResult[] = [{ ...result, savedAt: Date.now() }, ...prev];
      persistSaved(next);
      return next;
    });
  }, [persistSaved]);

  const removeFromLibrary = useCallback((id: number) => {
    setSavedItems(prev => {
      const next = prev.filter(s => s.id !== id);
      persistSaved(next);
      return next;
    });
  }, [persistSaved]);

  /* ─── search history helpers ─── */
  const persistHistory = useCallback((entries: HistoryEntry[]) => {
    try { localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(entries)); } catch { /* ignore */ }
  }, []);

  const addHistoryEntry = useCallback((q: string, count: number, filters: string[]) => {
    setSearchHistory(prev => {
      const entry: HistoryEntry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        query: q,
        timestamp: Date.now(),
        resultCount: count,
        filters,
      };
      const filtered = prev.filter(e => e.query.toLowerCase() !== q.toLowerCase());
      const next = [entry, ...filtered].slice(0, MAX_HISTORY);
      persistHistory(next);
      return next;
    });
  }, [persistHistory]);

  const deleteHistoryEntry = useCallback((id: string) => {
    setSearchHistory(prev => {
      const next = prev.filter(e => e.id !== id);
      persistHistory(next);
      return next;
    });
  }, [persistHistory]);

  const clearHistory = useCallback(() => {
    setSearchHistory([]);
    try { localStorage.removeItem(SEARCH_HISTORY_KEY); } catch { /* ignore */ }
  }, []);

  /* ─── search / generate ─── */
  const handleSearch = (overrideQuery?: string) => {
    const q = overrideQuery !== undefined ? overrideQuery : query;
    if (!q.trim()) return;
    if (overrideQuery !== undefined) setQuery(overrideQuery);
    if (isOver) {
      if (!emailCaptured) { setShowEmailCapture(true); } else { setShowUpgrade(true); }
      return;
    }
    setShowSuggestions(false);
    setShowHistoryPanel(false);
    setIsSearching(true);
    setTimeout(() => {
      /* count words in query + simulated result snippets */
      const consumed = countWords(q) + 38; /* ~avg snippet words */
      const newTotal = wordsUsed + consumed;
      setWordsUsed(newTotal);
      try { localStorage.setItem(WORDS_USED_KEY, String(newTotal)); } catch { /* ignore */ }
      if (!localStorage.getItem(FIRST_USAGE_KEY)) {
        localStorage.setItem(FIRST_USAGE_KEY, String(Date.now()));
      }
      const newResults = MOCK_RESULTS.map(r => ({ ...r, saved: savedItems.some(s => s.id === r.id) }));
      setResults(newResults);
      setResultFilter('');
      addHistoryEntry(q, newResults.length, [...activeFilters]);
      setIsSearching(false);
      setShowResults(true);
    }, 2000);
  };

  /* ─── save toggle — syncs with persistent library ─── */
  const toggleSave = (id: number) => {
    const result = results.find(r => r.id === id);
    if (!result) return;
    const alreadySaved = savedItems.some(s => s.id === id);
    if (alreadySaved) {
      removeFromLibrary(id);
    } else {
      addToLibrary(result);
    }
    setResults(prev => prev.map(r => r.id === id ? { ...r, saved: !alreadySaved } : r));
  };

  /* ─── cite helpers ─── */
  const openCite = (r: ResultItem) => { setCiteTarget(r); setCiteCopied(false); };
  const copyCitation = () => {
    if (!citeTarget) return;
    navigator.clipboard.writeText(CITATION_STYLES[citeStyle](citeTarget));
    setCiteCopied(true);
    setTimeout(() => setCiteCopied(false), 2000);
  };

  /* ─── words pill colors ─── */
  const pillBg = isOver ? 'bg-red-50 border-red-200' : isWarning ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200';
  const pillText = isOver ? 'text-red-700' : isWarning ? 'text-amber-700' : 'text-emerald-700';
  const barColor = isOver ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-[1800px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Breadcrumb />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                  <i className="ri-search-2-line text-white text-xl"></i>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900">Digital AI Search</h1>
                  <div className="flex items-center gap-2 mt-0.5">
                    <div className="flex items-center gap-1.5 px-2 py-0.5 bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 rounded-full">
                      <div className="w-1 h-1 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 animate-pulse"></div>
                      <span className="text-[10px] font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
                        OpenClaw Search Engine
                      </span>
                    </div>
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-cyan-50 rounded-full">
                      <i className="ri-global-line text-cyan-600 text-[10px]"></i>
                      <span className="text-[10px] font-medium text-cyan-700">Real-Time Web Access</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Navbar words pill */}
              <button
                onClick={() => isOver ? (emailCaptured ? setShowUpgrade(true) : setShowEmailCapture(true)) : undefined}
                className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${pillBg} ${pillText} ${isOver ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <i className={`text-xs ${isOver ? 'ri-lock-line' : isWarning ? 'ri-error-warning-line' : 'ri-edit-line'}`}></i>
                <span>{isOver ? 'Limit reached' : `${remaining.toLocaleString()} words left`}</span>
                <div className="w-12 h-1 bg-slate-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${barColor} ${isOver ? 'animate-pulse' : ''}`} style={{ width: `${usedPct}%` }}></div>
                </div>
              </button>

              {/* Saved Library button */}
              <button
                onClick={() => setShowLibrary(true)}
                className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700 text-xs font-semibold hover:bg-amber-100 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-bookmark-fill text-amber-500 text-sm"></i>
                <span>Library</span>
                {savedItems.length > 0 && (
                  <span className="w-5 h-5 flex items-center justify-center bg-amber-500 text-white rounded-full text-[10px] font-bold">
                    {savedItems.length > 9 ? '9+' : savedItems.length}
                  </span>
                )}
              </button>

              <ToolsDropdown isDarkMode={false} />
              <button
                onClick={() => setShowLogin(true)}
                className="text-sm font-semibold px-4 py-2 rounded-full border border-gray-200 text-gray-700 hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-4xl mx-auto px-6 pt-16 pb-12 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 rounded-full mb-6">
          <i className="ri-cpu-line text-violet-600"></i>
          <span className="text-sm font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
            Powered by OpenClaw Algorithm
          </span>
        </div>
        <h2 className="text-5xl font-bold text-slate-900 mb-4">
          Search Smarter with <span className="bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">OpenClaw AI</span>
        </h2>
        <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
          Get instant, accurate answers from the web with AI-powered search that understands context and delivers comprehensive results
        </p>

        {/* Trust Badges */}
        <div className="flex items-center justify-center gap-6 mb-10 flex-wrap">
          {[
            { icon: 'ri-flashlight-line', color: 'text-violet-600', label: 'Speed', value: '&lt;2s Response' },
            { icon: 'ri-checkbox-circle-line', color: 'text-emerald-600', label: 'Accuracy', value: '98.7% Verified' },
            { icon: 'ri-database-2-line', color: 'text-cyan-600', label: 'Source Depth', value: '50M+ Sources' },
          ].map((b, i) => (
            <div key={i} className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-slate-200">
              <i className={`${b.icon} ${b.color} text-lg`}></i>
              <div className="text-left">
                <div className="text-xs text-slate-500">{b.label}</div>
                <div className="text-sm font-bold text-slate-900" dangerouslySetInnerHTML={{ __html: b.value }}></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Trending Searches — below hero, above search box */}
      {!showResults && !isSearching && (
        <TrendingSearches
          onSearch={(q) => handleSearch(q)}
          wordsUsed={wordsUsed}
          freeLimit={FREE_WORD_LIMIT}
          todaySearchCount={todaySearchCount}
          topTopics={topTopics}
        />
      )}

      <div className="max-w-6xl mx-auto px-6 pb-16">
        {/* Search Section */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">AI-Powered Research at Your Fingertips</h2>
            <p className="text-xl text-gray-600">Find, summarize, and cite sources instantly with advanced AI</p>
          </div>

          <div className="max-w-3xl mx-auto">
            {/* Search Box */}
            <div
              ref={searchBoxRef}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onDrop={(e) => { e.preventDefault(); setIsDragging(false); handleFileSelect(e.dataTransfer.files); }}
              className={`bg-white rounded-2xl border-2 transition-all relative ${isDragging ? 'border-purple-500' : 'border-gray-200'}`}
            >
              <div className="p-6">
                <div className="flex gap-3 flex-wrap sm:flex-nowrap">
                  <div className="flex-1 min-w-0 relative">
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={query}
                      onChange={(e) => {
                        setQuery(e.target.value);
                        setShowSuggestions(e.target.value.trim().length >= 2);
                        setShowHistoryPanel(false);
                      }}
                      onFocus={() => {
                        if (query.trim().length >= 2) { setShowSuggestions(true); setShowHistoryPanel(false); }
                        else if (searchHistory.length > 0) { setShowHistoryPanel(true); setShowSuggestions(false); }
                      }}
                      placeholder="Ask anything… e.g. What are the latest developments in quantum computing?"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { handleSearch(); }
                        if (e.key === 'Escape') { setShowSuggestions(false); setShowHistoryPanel(false); }
                      }}
                    />

                    {/* Related Suggestions dropdown */}
                    <RelatedSearchSuggestions
                      query={query}
                      visible={showSuggestions}
                      onSelect={(s) => { setQuery(s); setShowSuggestions(false); }}
                    />

                    {/* Search History dropdown */}
                    <SearchHistoryPanel
                      entries={searchHistory}
                      visible={showHistoryPanel && !showSuggestions}
                      onSelect={(q) => { setQuery(q); setShowHistoryPanel(false); setTimeout(() => handleSearch(), 50); }}
                      onDelete={deleteHistoryEntry}
                      onClearAll={clearHistory}
                    />
                  </div>

                  <button
                    onClick={() => setShowAttachments(!showAttachments)}
                    title="Attach files"
                    className={`px-4 py-3 rounded-xl font-medium transition-all whitespace-nowrap cursor-pointer ${attachedFiles.length > 0 ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-attachment-2 text-lg"></i>
                    </div>
                    {attachedFiles.length > 0 && <span className="ml-1 text-sm">{attachedFiles.length}</span>}
                  </button>

                  <button
                    onClick={handleSearch}
                    disabled={isSearching || !query.trim()}
                    className={`px-6 py-3 font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer ${isOver ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 disabled:from-slate-300 disabled:to-slate-400 text-white'}`}
                  >
                    {isSearching ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>Searching…</span>
                      </>
                    ) : isOver ? (
                      <>
                        <div className="w-5 h-5 flex items-center justify-center"><i className="ri-vip-crown-line text-lg"></i></div>
                        <span>Upgrade to Search</span>
                      </>
                    ) : (
                      <>
                        <div className="w-5 h-5 flex items-center justify-center"><i className="ri-cpu-line text-lg"></i></div>
                        <span>Search with OpenClaw AI</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Words Remaining Indicator */}
                <div className="mt-3 flex items-center justify-between">
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium ${pillBg} ${pillText}`}>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`text-xs ${isOver ? 'ri-lock-line' : isWarning ? 'ri-error-warning-line' : 'ri-edit-line'}`}></i>
                    </div>
                    <span>{isOver ? 'Free limit reached' : `${remaining.toLocaleString()} free words left`}</span>
                    <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${usedPct}%` }}></div>
                    </div>
                  </div>
                  {isOver ? (
                    <button onClick={() => emailCaptured ? setShowUpgrade(true) : setShowEmailCapture(true)} className="text-xs text-violet-600 hover:text-violet-700 font-semibold cursor-pointer whitespace-nowrap">
                      Upgrade for unlimited →
                    </button>
                  ) : (
                    <div className="hidden sm:flex items-center gap-1.5">
                      <button
                        onClick={() => setShowShortcutsLegend(v => !v)}
                        className="flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                        title="Keyboard shortcuts"
                      >
                        <i className="ri-keyboard-line text-xs"></i>
                        <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-mono font-bold text-slate-500">⌘K</kbd>
                        <span className="text-[10px]">to focus</span>
                        <span className="text-slate-300">·</span>
                        <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-mono font-bold text-slate-500">?</kbd>
                        <span className="text-[10px]">for all shortcuts</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Attachments Panel */}
                {showAttachments && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-sm font-medium text-gray-700">
                        Attachments {attachedFiles.length > 0 && `(${attachedFiles.length}/10)`}
                      </label>
                      <button onClick={() => fileInputRef.current?.click()} className="text-sm text-purple-600 hover:text-purple-700 font-medium whitespace-nowrap cursor-pointer">
                        + Add Files
                      </button>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,video/*,.pdf,.docx,.txt,.csv"
                      onChange={(e) => handleFileSelect(e.target.files)}
                      className="hidden"
                    />
                    {attachedFiles.length === 0 ? (
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center bg-gray-50">
                        <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                          <i className="ri-upload-cloud-2-line text-3xl text-gray-400"></i>
                        </div>
                        <p className="text-sm text-gray-600">Drag &amp; drop files here or click "Add Files"</p>
                        <p className="text-xs text-gray-500 mt-1">Images, Videos, PDF, DOCX, TXT, CSV • Max 25MB • Max 10 files</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {attachedFiles.some(f => f.preview) && (
                          <div className="grid grid-cols-6 gap-2">
                            {attachedFiles.filter(f => f.preview).map(({ file, preview, id }) => (
                              <div key={id} className="relative group">
                                <img src={preview} alt={file.name} className="w-full h-16 object-cover rounded-lg border border-gray-200" />
                                <button onClick={() => removeFile(id)} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                                  <i className="ri-close-line text-xs"></i>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {attachedFiles.filter(f => !f.preview).map(({ file, id }) => (
                          <div key={id} className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded-lg group hover:border-gray-300 transition-colors">
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className={`${getFileIcon(file.type)} text-xl text-gray-400`}></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-900 truncate">{file.name}</p>
                              <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                            </div>
                            <button onClick={() => removeFile(id)} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100 cursor-pointer">
                              <i className="ri-close-line text-sm"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Quick Filters */}
            <div className="flex items-center gap-3 mt-4 justify-center flex-wrap">
              <span className="text-sm text-gray-500 font-medium">Filter by:</span>
              {['Academic Papers', 'News Articles', 'Research Studies', 'Books', 'Videos'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => toggleFilter(filter)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer border ${
                    activeFilters.has(filter)
                      ? 'bg-violet-600 text-white border-violet-600'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-purple-300 hover:bg-purple-50'
                  }`}
                >
                  {activeFilters.has(filter) && (
                    <i className="ri-check-line mr-1 text-xs"></i>
                  )}
                  {filter}
                </button>
              ))}
              {activeFilters.size > 0 && (
                <button onClick={() => setActiveFilters(new Set())} className="text-xs text-gray-500 hover:text-gray-700 underline cursor-pointer whitespace-nowrap">
                  Clear all
                </button>
              )}
            </div>

            {/* Advanced Search toggle */}
            <div className="mt-3 flex justify-center">
              <button
                onClick={() => setShowAdvanced(v => !v)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  showAdvanced || advActiveCount > 0
                    ? 'bg-slate-800 text-white border-slate-800'
                    : 'bg-white border-slate-300 text-slate-600 hover:border-slate-500 hover:text-slate-800'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-equalizer-2-line text-sm`}></i>
                </div>
                Advanced Search
                {advActiveCount > 0 && (
                  <span className="inline-flex items-center justify-center w-5 h-5 bg-violet-500 text-white text-[10px] font-bold rounded-full">
                    {advActiveCount}
                  </span>
                )}
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className={`ri-arrow-${showAdvanced ? 'up' : 'down'}-s-line text-xs`}></i>
                </div>
              </button>
            </div>

            {/* Advanced Search Panel */}
            {showAdvanced && (
              <AdvancedSearchPanel
                filters={advFilters}
                onDateFromChange={(year) => setAdvFilters(f => ({ ...f, dateFrom: year }))}
                onDateToChange={(year) => setAdvFilters(f => ({ ...f, dateTo: year }))}
                onToggleExcludeType={(type) => setAdvFilters(f => {
                  const next = new Set(f.excludedTypes);
                  next.has(type) ? next.delete(type) : next.add(type);
                  return { ...f, excludedTypes: next };
                })}
                onMinCitationsChange={(n) => setAdvFilters(f => ({ ...f, minCitations: n }))}
                onReset={resetAdvFilters}
              />
            )}
          </div>
        </div>

        {/* Loading */}
        {isSearching && (
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
              <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-4"></div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Searching…</h3>
              <p className="text-gray-600">AI is analyzing millions of sources</p>
            </div>
          </div>
        )}

        {/* Results */}
        {showResults && !isSearching && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center">
                  <i className="ri-search-2-line text-white text-lg"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Search Results</h3>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <div className="flex items-center gap-1.5 px-2 py-0.5 bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 rounded-full">
                      <i className="ri-cpu-line text-violet-600 text-xs"></i>
                      <span className="text-xs font-medium text-violet-700">OpenClaw Search Engine</span>
                    </div>
                    <span className="text-xs text-slate-500">Found {results.length} results in 1.8s</span>
                    {activeFilters.size > 0 && (
                      <span className="text-xs text-purple-600 font-medium">
                        Filtered: {[...activeFilters].join(', ')}
                      </span>
                    )}
                    {advActiveCount > 0 && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-slate-800 text-white rounded-full text-[10px] font-bold">
                        <i className="ri-equalizer-2-line text-[10px]"></i>
                        {advActiveCount} advanced filter{advActiveCount > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => { setShowResults(false); setQuery(''); setResults([]); setActiveFilters(new Set()); setResultFilter(''); resetAdvFilters(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-600 font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-refresh-line text-sm"></i></div>
                New Search
              </button>
            </div>

            {/* AI Summary */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-200 p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-sparkling-line text-2xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">AI Summary</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Quantum computing represents a revolutionary approach to computation that leverages quantum mechanical phenomena. Recent developments include Google&apos;s achievement of quantum supremacy, IBM&apos;s advancement in error correction, and significant progress in quantum algorithms for cryptography and optimization problems.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <span className="px-3 py-1 bg-white border border-purple-200 text-purple-700 rounded-full text-sm font-medium">5 sources cited</span>
                    <span className="px-3 py-1 bg-white border border-purple-200 text-purple-700 rounded-full text-sm font-medium">95% confidence</span>
                    <button
                      onClick={() => { const blob = new Blob(['AI Summary\n\nQuantum computing represents a revolutionary approach...'], { type: 'text/plain' }); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'ai-summary.txt'; a.click(); }}
                      className="px-3 py-1 bg-white border border-purple-200 text-purple-700 rounded-full text-sm font-medium hover:bg-purple-50 transition-colors cursor-pointer flex items-center gap-1"
                    >
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-download-line text-xs"></i></div>
                      Export Summary
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Search Within Results filter bar */}
            <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 flex-shrink-0">
                <i className="ri-filter-3-line text-slate-500 text-base"></i>
              </div>
              <div className="flex-1 relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none"></i>
                <input
                  ref={resultFilterRef}
                  type="text"
                  value={resultFilter}
                  onChange={(e) => setResultFilter(e.target.value)}
                  placeholder="Filter results by keyword, source, or type…"
                  className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
                />
              </div>
              {resultFilter && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${filteredResults.length > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                    {filteredResults.length}/{results.length}
                  </span>
                  <button
                    onClick={() => setResultFilter('')}
                    className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                </div>
              )}
              {!resultFilter && (
                <span className="text-xs text-slate-400 flex-shrink-0 hidden sm:block">
                  {results.length} result{results.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {/* No results message */}
            {filteredResults.length === 0 && (resultFilter || advActiveCount > 0) && (
              <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center">
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-search-line text-2xl text-slate-400"></i>
                </div>
                <p className="text-sm font-bold text-slate-700 mb-1">
                  {resultFilter ? `No results match "${resultFilter}"` : 'No results match your active filters'}
                </p>
                <p className="text-xs text-slate-500">
                  Try adjusting your filters or{' '}
                  <button
                    onClick={() => { setResultFilter(''); resetAdvFilters(); }}
                    className="text-violet-600 underline cursor-pointer"
                  >
                    clear all filters
                  </button>
                </p>
              </div>
            )}

            {/* Result Cards */}
            <div className="grid gap-4">
              {filteredResults.map((result) => (
                <div key={result.id} className="bg-white rounded-xl border border-gray-200 p-6 hover:border-purple-300 transition-all cursor-pointer group">
                  <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                    <h4 className="text-lg font-bold text-gray-900 flex-1 group-hover:text-violet-700 transition-colors">{result.title}</h4>
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium whitespace-nowrap">{result.type}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3 flex-wrap">
                    <span className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-book-line text-sm"></i></div>
                      {result.source}
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line text-sm"></i></div>
                      {result.date}
                    </span>
                    <span className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-100 rounded-full">
                      <div className="w-3 h-3 flex items-center justify-center"><i className="ri-double-quotes-r text-xs text-slate-500"></i></div>
                      <span className="text-xs font-semibold text-slate-600">{result.citations.toLocaleString()} citations</span>
                    </span>
                  </div>
                  <p className="text-gray-700 mb-4 text-sm leading-relaxed">{result.snippet}</p>
                  <div className="flex gap-2 flex-wrap">
                    {/* Cite */}
                    <button
                      onClick={() => openCite(result)}
                      className="px-4 py-2 bg-violet-50 text-violet-700 border border-violet-200 rounded-lg text-sm font-medium hover:bg-violet-100 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5"
                    >
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-double-quotes-r text-sm"></i></div>
                      Cite
                    </button>
                    {/* Save */}
                    <button
                      onClick={() => toggleSave(result.id)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 border ${result.saved ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100' : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'}`}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={result.saved ? 'ri-bookmark-fill text-sm' : 'ri-bookmark-line text-sm'}></i>
                      </div>
                      {result.saved ? 'Saved' : 'Save'}
                    </button>
                    {/* Open */}
                    <button
                      onClick={() => setOpenTarget(result)}
                      className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5"
                    >
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-external-link-line text-sm"></i></div>
                      Open
                    </button>
                    {/* Sync to Paper Writer */}
                    <button
                      onClick={() => window.open('/paper-writer', '_self')}
                      className="ml-auto px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-sm font-medium hover:bg-emerald-100 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5"
                    >
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-file-transfer-line text-sm"></i></div>
                      Use in Paper Writer
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Saved results summary */}
            {results.some(r => r.saved) && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center"><i className="ri-bookmark-fill text-amber-600"></i></div>
                  <span className="text-sm font-medium text-amber-800">
                    {results.filter(r => r.saved).length} result{results.filter(r => r.saved).length > 1 ? 's' : ''} saved to your library
                  </span>
                </div>
                <button
                  onClick={() => setShowLibrary(true)}
                  className="text-xs text-amber-700 font-semibold hover:text-amber-900 cursor-pointer whitespace-nowrap underline"
                >
                  View Library →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Features Section */}
        {!showResults && !isSearching && (
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { icon: 'ri-search-eye-line', title: 'Smart Search', description: 'AI understands context and intent to find the most relevant sources' },
              { icon: 'ri-file-list-3-line', title: 'Auto Citations', description: 'Generate properly formatted citations in APA, MLA, or Chicago style instantly' },
              { icon: 'ri-lightbulb-line', title: 'AI Summaries', description: 'Get concise summaries of complex research papers and articles' },
            ].map((feature, idx) => (
              <div key={idx} className="bg-white rounded-2xl border border-gray-200 p-6 text-center hover:border-purple-200 transition-all">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className={`${feature.icon} text-2xl text-purple-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Cite Modal ── */}
      {citeTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Generate Citation</h3>
              <button onClick={() => setCiteTarget(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer">
                <i className="ri-close-line text-gray-500 text-xl"></i>
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4 truncate font-medium">{citeTarget.title}</p>

            {/* Style tabs */}
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-4">
              {(['APA', 'MLA', 'Chicago'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => { setCiteStyle(s); setCiteCopied(false); }}
                  className={`flex-1 py-2 rounded-md text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${citeStyle === s ? 'bg-white text-violet-700' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {s}
                </button>
              ))}
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-700 leading-relaxed mb-4 select-all">
              {CITATION_STYLES[citeStyle](citeTarget)}
            </div>

            <div className="flex gap-3">
              <button
                onClick={copyCitation}
                className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap ${citeCopied ? 'bg-emerald-500 text-white' : 'bg-violet-600 hover:bg-violet-700 text-white'}`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`text-sm ${citeCopied ? 'ri-check-line' : 'ri-clipboard-line'}`}></i>
                </div>
                {citeCopied ? 'Copied!' : 'Copy Citation'}
              </button>
              <button onClick={() => setCiteTarget(null)} className="px-5 py-2.5 rounded-xl font-semibold text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 transition-all cursor-pointer whitespace-nowrap">
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Open Source Preview Modal ── */}
      {openTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Source Preview</h3>
              <button onClick={() => setOpenTarget(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer">
                <i className="ri-close-line text-gray-500 text-xl"></i>
              </button>
            </div>
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2.5 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">{openTarget.type}</span>
              <span className="text-sm text-gray-500">{openTarget.source} · {openTarget.date}</span>
            </div>
            <h4 className="text-base font-bold text-gray-900 mb-3">{openTarget.title}</h4>
            <p className="text-sm text-gray-600 leading-relaxed mb-5">{openTarget.snippet}</p>
            <div className="flex gap-3">
              <a
                href={openTarget.url}
                target="_blank"
                rel="nofollow noreferrer"
                className="flex-1 py-2.5 rounded-xl font-semibold text-sm bg-violet-600 hover:bg-violet-700 text-white transition-all cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-external-link-line text-sm"></i></div>
                Open Full Source
              </a>
              <button
                onClick={() => { toggleSave(openTarget.id); setOpenTarget(null); }}
                className="px-5 py-2.5 rounded-xl font-semibold text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={results.find(r => r.id === openTarget.id)?.saved ? 'ri-bookmark-fill text-amber-500' : 'ri-bookmark-line'}></i>
                </div>
                {results.find(r => r.id === openTarget.id)?.saved ? 'Saved' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shared Modals */}
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
      {showUpgrade && (
        <CheckoutModal
          plan={{ name: 'Pro', price: '$12', annualPrice: '$8', period: '/mo', annualPeriod: '/mo billed annually', features: ['Unlimited words', 'All AI tools', 'Priority support', 'Export in all formats'] }}
          billingCycle="monthly"
          onClose={() => setShowUpgrade(false)}
        />
      )}
      {showEmailCapture && (
        <EmailCaptureModal
          wordsUsed={wordsUsed}
          freeLimit={FREE_WORD_LIMIT}
          onContinue={(email) => {
            if (email) { /* email submitted */ }
            localStorage.setItem(EMAIL_CAPTURED_KEY, '1');
            setEmailCaptured(true);
            setShowEmailCapture(false);
            setTimeout(() => setShowUpgrade(true), 1200);
          }}
          onClose={() => {
            localStorage.setItem(EMAIL_CAPTURED_KEY, '1');
            setEmailCaptured(true);
            setShowEmailCapture(false);
            setShowUpgrade(true);
          }}
        />
      )}

      {/* ── Keyboard Shortcuts Legend ── */}
      <KeyboardShortcutsLegend
        open={showShortcutsLegend}
        onToggle={() => setShowShortcutsLegend(v => !v)}
        onClose={() => setShowShortcutsLegend(false)}
      />

      {/* ── Saved Library Drawer ── */}
      <SavedLibraryDrawer
        open={showLibrary}
        items={savedItems}
        onClose={() => setShowLibrary(false)}
        onRemove={removeFromLibrary}
        onCite={(item) => {
          setShowLibrary(false);
          setCiteTarget(item as unknown as ResultItem);
          setCiteCopied(false);
        }}
        onOpen={(item) => {
          setShowLibrary(false);
          setOpenTarget(item as unknown as ResultItem);
        }}
      />
    </div>
  );
}
